import { Dispatch, ReactNode, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { isEqual } from "lodash";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { Banner } from "@foodpilot/foods";

import { Product } from "src/api/client-api/products/useGetOneProduct";
import { useUpsertProduct } from "src/api/client-api/products/useUpsertProduct";
import { useUploadProductPhoto } from "src/api/client-api/products/useProductPhotos";
import { ProductReference } from "src/api/sure-api/productReference/useGetOneProductReference";
import { productGridKey } from "src/api/client-api/products/useGetAllproduct";
import { getOneProductKey } from "src/api/client-api/products/useGetOneProduct";
import { getProductScoresKey } from "src/api/client-api/olapScores/useGetProductScores";
import { ProductService } from "src/pages/Product/ProductService";
import { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { URLHandler } from "src/services/URLHandler";
import {
    ConfirmationModalData,
    DataDeletionWarningDialog,
} from "src/components/Dialog/Templates/DataDeletionWarningDialog";
import { SsqPostChangedWarnings } from "src/components/Dialog/Templates/AllWarnings/SsqPostChangedWarnings";
import { AssociatedStructureWarning } from "src/components/Dialog/Templates/AllWarnings/AssociatedStructureWarning";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { OlapScoresWithPrevScores } from "src/api/client-api/olapScores/olapScoresSchema";
import { useSnackbar } from "src/utils/useSnackbar";
import { useWizardStepsV2, WizardStepKey } from "./useWizardStepsV2";
import { ScoreLiveSidebar } from "./ScoreLiveSidebar/ScoreLiveSidebar";
import { EntityFormPage } from "src/components/EntityForm";
import { ProductFormHeader } from "./Layout/ProductFormHeader";
import { QuitConfirmDialog } from "./Layout/QuitConfirmDialog";
import { validateProductForm, SectionErrors } from "./validation";
import { mergeSectionErrors, productViolationsToSectionErrors } from "./saveViolations";
import { ProductValidationBanner } from "./Layout/ProductValidationBanner";
import {
    buildPersistedDraftProductTransports,
    mergeResolvedDraftProductTransports,
} from "./Steps/draftProductTransports";
import { EntityScoresProvider } from "src/context/Scores/provider";
import { ProductScoresContextProvider } from "src/context/Product/Scores/provider";
import { useProductMetadataContext } from "src/context/Product/Metadata/useProductMetadata";
import { useProductFormScores } from "./scores/useProductFormScores";
import type { TransportMaterializationPendingChange } from "./Steps/ProductTransportInlineSelector";
import { ProductFormSections } from "./ProductFormSections";
import { useInitialProductSectionScroll, useProductFormBreadcrumbs } from "./useProductFormNavigation";
import { useProductFormLeaveGuard } from "./useProductFormLeaveGuard";

type ProductFormV2Props = {
    existingProduct: Product | undefined;
    companyIds: { id: number; "@id": IRI; name: string };
    productReference: ProductReference | undefined;
    scores?: OlapScoresWithPrevScores;
    startingPageId: string;
    isIntermediate?: boolean;
};

const getProductDraftValues = (product: ProductFormType) => {
    const { photos: _photos, pendingProductPhotos, ...draft } = product;
    return { ...draft, pendingProductPhotos: pendingProductPhotos ?? [] };
};

/**
 * V2 product form (Figma `12819:67393` "Product creation - Filled").
 *
 * Long-scroll layout: breadcrumb + page header (title / Cancel / Save) + 2-column main
 * (left = stacked `SectionCard`s, right = sticky `ScoreLiveSidebar`). Replaces the
 * previous wizard shell (`FormBaseContextProvider` + `FormTopBar`) per the
 * `product-form-v2-figma-fidelity` plan.
 *
 * Behavior:
 *  - Sections are filtered dynamically via `useWizardStepsV2()` (jsonAttrs +
 *    distribution + consumption flags).
 *  - Save runs `validateProductForm()` first; on failure the page scrolls to the
 *    first invalid section. On success the existing `DataDeletionWarning` /
 *    `SsqPostChangedWarnings` / `AssociatedStructureWarning` flow is preserved.
 *  - Cancel opens `QuitConfirmDialog` (Figma `12580:47319` copy).
 *  - `startingPageId` is accepted to keep the v1 routing signature (`/edit/:startingPageId`)
 *    but in V2 it is interpreted as a section anchor key — see `scrollToSection`.
 */
export const ProductFormV2 = (props: ProductFormV2Props) => {
    const { existingProduct } = props;

    if (existingProduct !== undefined) {
        return (
            <ProductFormV2ExistingProductShell existingProduct={existingProduct}>
                <ProductFormV2Content {...props} />
            </ProductFormV2ExistingProductShell>
        );
    }

    return (
        <EntityScoresProvider entity="product">
            <ProductFormV2Content {...props} />
        </EntityScoresProvider>
    );
};

type ProductFormV2ExistingProductShellProps = {
    existingProduct: Product;
    children: ReactNode;
};

const ProductFormV2ExistingProductShell = (props: ProductFormV2ExistingProductShellProps) => {
    const { existingProduct, children } = props;
    const { ssqVersion, isSsqVersionReady, isActivePostLoading } = useProductMetadataContext();

    return (
        <ProductScoresContextProvider
            product={existingProduct}
            ssq={ssqVersion?.ssq}
            isSsqReady={isSsqVersionReady}
            isActivePostLoading={isActivePostLoading}
        >
            {children}
        </ProductScoresContextProvider>
    );
};

const ProductFormV2Content = (props: ProductFormV2Props) => {
    const { existingProduct, productReference, startingPageId, companyIds, isIntermediate = false, scores } = props;
    const { t } = useTranslation();
    const location = useLocation();
    const snackbar = useSnackbar();
    const upsertProduct = useUpsertProduct({ quarantineAmbiguousCreate: true });
    const uploadProductPhoto = useUploadProductPhoto();
    const queryClient = useQueryClient();
    const { isFeatureActivated } = useApplicationParameters();
    const isProductDistributionEnabled = isFeatureActivated("FS-product-distribution");
    const isProductConsumptionEnabled = isFeatureActivated("FS-product-consumption");
    const productFormWriteOptions = {
        isProductDistributionEnabled,
        isProductConsumptionEnabled,
    };

    const formData = ProductService.getProductToCreate({ existingProduct, productReference });
    // For a new product, default `ownedBy` to the active company so the form state
    // matches what `productFormToProductWrite` will send anyway, and so that
    // `validateProductForm` passes when the Organization section is hidden by
    // `useOrganizationSectionVisibility` (no UI to set ownedBy in that case).
    const initialProduct: ProductFormType = {
        ...formData,
        isIntermediate: formData.id !== undefined ? formData.isIntermediate : isIntermediate,
        ownedBy:
            formData.ownedBy ??
            (existingProduct === undefined ?
                { "@id": companyIds["@id"], id: companyIds.id, name: companyIds.name }
            :   null),
    };
    const [product, setProduct] = useState<ProductFormType>(initialProduct);
    const initialProductRef = useRef(getProductDraftValues(initialProduct));

    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get("returnUrl");
    const sectionAnchor = searchParams.get("section");
    const canonicalProductListUrl =
        isIntermediate ? URLHandler.intermediateProduct.list(companyIds.id) : URLHandler.product.list(companyIds.id);
    const productListUrl = returnUrl ? decodeURIComponent(returnUrl) : canonicalProductListUrl;

    const [warningModalState, setWarningModalState] = useState<ConfirmationModalData>({
        isOpen: false,
        errors: null,
    });
    const [validationAttempt, setValidationAttempt] = useState(0);
    const [serverLineErrors, setServerLineErrors] = useState<SectionErrors>({});
    useEffect(() => {
        setServerLineErrors({});
    }, [product.productIngredients, product.productPackagings]);
    const validationBannerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (validationAttempt === 0) return;
        validationBannerRef.current?.focus({ preventScroll: true });
        validationBannerRef.current?.scrollIntoView({ block: "start" });
    }, [validationAttempt]);
    const submissionInFlightRef = useRef(false);
    const [isSubmissionInFlight, setIsSubmissionInFlight] = useState(false);
    const photoPersistenceInFlightRef = useRef(false);
    const [isPhotoPersistenceInFlight, setIsPhotoPersistenceInFlight] = useState(false);
    const onPhotoPersistencePendingChange = useCallback((pending: boolean) => {
        photoPersistenceInFlightRef.current = pending;
        setIsPhotoPersistenceInFlight(pending);
    }, []);
    const [pendingTransportSelectorIds, setPendingTransportSelectorIds] = useState<ReadonlySet<string>>(
        () => new Set(),
    );
    const onTransportMaterializationPendingChange = useCallback<TransportMaterializationPendingChange>(
        (selectorId, pending) => {
            setPendingTransportSelectorIds((current) => {
                if (current.has(selectorId) === pending) return current;

                const next = new Set(current);
                if (pending) next.add(selectorId);
                else next.delete(selectorId);
                return next;
            });
        },
        [],
    );
    const isTransportMaterializationPending = pendingTransportSelectorIds.size > 0;
    const isSubmissionPersistenceInFlight =
        isSubmissionInFlight || upsertProduct.isLoading || uploadProductPhoto.isLoading;
    const isPersistenceInFlight = isSubmissionPersistenceInFlight || isPhotoPersistenceInFlight;
    const isDraftWriteBlocked = isPersistenceInFlight || upsertProduct.isCreateIdentityUnknown;
    const setProductIfEditable = useCallback<Dispatch<SetStateAction<ProductFormType>>>(
        (update) => {
            if (
                submissionInFlightRef.current ||
                isSubmissionPersistenceInFlight ||
                upsertProduct.isCreateIdentityUnknown
            ) {
                return;
            }
            setProduct(update);
        },
        [isSubmissionPersistenceInFlight, upsertProduct.isCreateIdentityUnknown],
    );
    const isExitCurrentlyBlocked = useCallback(
        () =>
            submissionInFlightRef.current ||
            photoPersistenceInFlightRef.current ||
            upsertProduct.isLoading ||
            uploadProductPhoto.isLoading ||
            isTransportMaterializationPending,
        [isTransportMaterializationPending, uploadProductPhoto.isLoading, upsertProduct.isLoading],
    );
    const isExitBlocked = isPersistenceInFlight || isTransportMaterializationPending;
    const leaveGuard = useProductFormLeaveGuard({
        dirty: !isEqual(getProductDraftValues(product), initialProductRef.current),
        busy: isExitBlocked,
    });
    // Tracking des entités supprimées par rapport à l'état initial — strictement identique à v1.
    const productCompanies = product.shares.map((pC) => pC.organization);
    const initEntities = useRef(productCompanies);
    const newItemsIds = productCompanies.map((entity) => entity.id);
    const deletedEntities = initEntities.current
        .filter((i) => !newItemsIds.includes(i.id))
        .map((i) => i.name)
        .join(",");

    useEffect(() => {
        if (existingProduct && product.id === undefined) {
            const updatedFormData = ProductService.getProductToCreate({ existingProduct, productReference });
            setProduct(updatedFormData);
        }
    }, [existingProduct, productReference, product.id]);

    const stepKeys = useWizardStepsV2(companyIds);
    const validationOptions = { validateRecipe: stepKeys.includes("recipe") };
    const validationErrors: SectionErrors =
        validationAttempt > 0 ? validateProductForm(product, validationOptions).errorsBySection : {};
    const sectionErrors = mergeSectionErrors(validationErrors, serverLineErrors);
    const validateBeforeSave = () => {
        const validation = validateProductForm(product, validationOptions);
        if (!validation.success) setValidationAttempt((attempt) => attempt + 1);
        return validation.success;
    };
    const isNew = product.id === undefined;
    const formScores = useProductFormScores(product, existingProduct, { persistedScores: scores?.olapScores });

    const {
        selectedUnit: scoreUnit = "Pts",
        getDisplayedSectionScore,
        ingredientLineScores,
        ingredientEcsBreakdown,
        packagingLineScores,
        transformationLineScores,
        transportLineScores,
        resolvedTransports,
        olapScores,
        productReferenceScores,
        mode: scoresMode,
        isLoading: scoresLoading,
        scoreQueryError,
        retryScoreQuery,
    } = formScores;

    // One stable ref per section key. Allocated upfront so each `SectionCard` keeps
    // the same DOM ref across renders — the list of mounted sections can shrink based
    // on flag changes without invalidating the references the scroll helpers rely on.
    const identityRef = useRef<HTMLDivElement>(null);
    const additionalInfoRef = useRef<HTMLDivElement>(null);
    const organizationRef = useRef<HTMLDivElement>(null);
    const recipeRef = useRef<HTMLDivElement>(null);
    const transformationRef = useRef<HTMLDivElement>(null);
    const packagingRef = useRef<HTMLDivElement>(null);
    const transportRef = useRef<HTMLDivElement>(null);
    const distributionRef = useRef<HTMLDivElement>(null);
    const consumptionRef = useRef<HTMLDivElement>(null);
    const refsByKey: Record<WizardStepKey, RefObject<HTMLDivElement | null>> = {
        identity: identityRef,
        additionalInformations: additionalInfoRef,
        organization: organizationRef,
        recipe: recipeRef,
        transformation: transformationRef,
        packaging: packagingRef,
        transport: transportRef,
        distribution: distributionRef,
        consumption: consumptionRef,
    };

    // Backwards-compat scroll-to-section logic, runs once at mount:
    //  - If `?section=key` is present, scroll the matching V2 section.
    //  - Otherwise, interpret `:startingPageId` as the numeric index used by the v1
    //    wizard (Identity=0, Recipe=1, Packaging=2, Distribution|Consumption=3,
    //    Consumption=4) and scroll the matching V2 section.
    // This keeps the existing "Modifier" deep-links from `ProductInformations` v1/V2
    // working without altering URLHandler.
    useInitialProductSectionScroll({
        isProductDistributionEnabled,
        refsByKey,
        sectionAnchor,
        startingPageId,
    });

    const requestExit = useCallback(
        (destination: string) => {
            if (isExitCurrentlyBlocked()) return;
            leaveGuard.requestExit(destination);
        },
        [isExitCurrentlyBlocked, leaveGuard.requestExit],
    );

    const breadcrumbs = useProductFormBreadcrumbs({
        canonicalProductListUrl,
        companyId: companyIds.id,
        existingProduct,
        exitDisabled: isExitBlocked,
        isIntermediate,
        isNew,
        requestExit,
        t,
    });

    const handleProductUpdate = async () => {
        // This function is also called by the warning-dialog confirmation. Keep
        // the persistence boundary guarded even when callers bypass handleSave.
        if (
            submissionInFlightRef.current ||
            photoPersistenceInFlightRef.current ||
            upsertProduct.isCreateIdentityUnknown ||
            isTransportMaterializationPending
        ) {
            return;
        }
        if (!validateBeforeSave()) {
            leaveGuard.cancelSave();
            return;
        }
        submissionInFlightRef.current = true;
        setIsSubmissionInFlight(true);
        setServerLineErrors({});

        try {
            const options = {
                product: ProductService.productFormToProductWrite(product, undefined, productFormWriteOptions),
                organizationId: companyIds.id,
            };

            const response = await upsertProduct.mutateAsync(options);
            queryClient.invalidateQueries([productGridKey]);

            let productId = response?.data?.id || product.id;
            if (existingProduct === undefined && response?.data !== undefined) {
                const draftProductTransports = product.productTransports?.filter(
                    (transport) => transport.draftProductIngredientKey != null,
                );
                const persistedProduct = ProductService.getProductToCreate({
                    existingProduct: response.data as Product,
                    productReference,
                });
                const draftTransportResolution =
                    draftProductTransports !== undefined && draftProductTransports.length > 0 ?
                        buildPersistedDraftProductTransports(
                            draftProductTransports,
                            persistedProduct.productIngredients,
                        )
                    :   null;
                const resumableProduct = {
                    ...persistedProduct,
                    pendingProductPhotos: product.pendingProductPhotos ?? [],
                    productTransports:
                        draftTransportResolution === null ?
                            persistedProduct.productTransports
                        :   mergeResolvedDraftProductTransports(
                                persistedProduct.productTransports,
                                draftTransportResolution,
                            ),
                };

                // Adopt the persisted identity immediately after the successful create.
                // Any later transport or photo failure must resume with a PUT, never
                // create a duplicate product with another POST.
                setProduct(resumableProduct);

                if (draftTransportResolution !== null) {
                    if (draftTransportResolution.unmatchedTransports.length > 0) {
                        snackbar.setSnackbarProps({
                            type: "error",
                            message: t(
                                "ingredientSupplier.transport.saveError",
                                "The transport circuits could not be saved. Please try again.",
                            ),
                        });
                        leaveGuard.cancelSave();
                        return;
                    }

                    if (draftTransportResolution.persistedTransports.length > 0) {
                        const persistedResponse = await upsertProduct.mutateAsync({
                            product: ProductService.productFormToProductWrite(
                                resumableProduct,
                                undefined,
                                productFormWriteOptions,
                            ),
                            organizationId: companyIds.id,
                        });
                        productId = persistedResponse?.data?.id || productId;
                        if (persistedResponse?.data !== undefined) {
                            const persistedProductWithTransports = ProductService.getProductToCreate({
                                existingProduct: persistedResponse.data as Product,
                                productReference,
                            });
                            setProduct({
                                ...persistedProductWithTransports,
                                pendingProductPhotos: resumableProduct.pendingProductPhotos,
                            });
                        }
                    }
                }
            }

            if (productId !== undefined) {
                queryClient.invalidateQueries([getOneProductKey, productId]);
                queryClient.invalidateQueries([getProductScoresKey, productId]);
            }

            if (productId) {
                const pendingProductPhotos = product.pendingProductPhotos ?? [];
                await Promise.all(
                    pendingProductPhotos.map((file, index) =>
                        uploadProductPhoto.mutateAsync({
                            productId,
                            file,
                            isPrimary: index === 0,
                            replacePhotos: index === 0 ? (product.photos ?? []) : [],
                        }),
                    ),
                );

                const redirectUrl =
                    product.isIntermediate ?
                        URLHandler.intermediateProduct.scores(companyIds.id, productId)
                    :   URLHandler.product.scores(companyIds.id, productId);
                leaveGuard.completeSave(redirectUrl);
            } else {
                const fallbackUrl =
                    product.isIntermediate ? URLHandler.intermediateProduct.list(companyIds.id) : productListUrl;
                leaveGuard.completeSave(fallbackUrl);
            }
        } catch (error) {
            leaveGuard.cancelSave();
            console.error(error);
            setServerLineErrors(productViolationsToSectionErrors(error));
            snackbar.setSnackbarProps({
                type: "error",
                message: t("product.form.save_error", "Unable to save the product."),
            });
        } finally {
            submissionInFlightRef.current = false;
            setIsSubmissionInFlight(false);
        }
    };

    const handleSave = () => {
        if (photoPersistenceInFlightRef.current || isTransportMaterializationPending) return;

        if (!validateBeforeSave()) {
            leaveGuard.cancelSave();
            return;
        }

        // Identical to v1 ProductForm.onValidate — keep both warnings active in V2.
        const errors = [];
        const hasSsqChanged = existingProduct?.ssqId !== undefined && existingProduct.ssqId !== product.ssq_id;
        if (hasSsqChanged) {
            errors.push(
                <SsqPostChangedWarnings
                    sharedOrganizationNames={existingProduct?.shares?.map((share) => share.organization.name)}
                />,
            );
        }
        if (deletedEntities.length > 0) {
            errors.push(<AssociatedStructureWarning deletedEntities={deletedEntities} />);
        }

        if (errors.length > 0) {
            setWarningModalState({ isOpen: true, errors });
            return;
        }

        handleProductUpdate();
    };

    return (
        <>
            <DataDeletionWarningDialog
                confirmationData={warningModalState}
                setConfirmationData={(nextState) => {
                    setWarningModalState(nextState);
                    if (!nextState.isOpen && !submissionInFlightRef.current) leaveGuard.cancelSave();
                }}
                onConfirm={() => {
                    handleProductUpdate();
                }}
            />

            <EntityFormPage
                header={(ref) => (
                    <ProductFormHeader
                        ref={ref}
                        breadcrumbs={breadcrumbs}
                        title={
                            isNew ?
                                t("product.v2.form.creation_title", "New product")
                            :   t("product.form.modification_title", "Modify the product")
                        }
                        owningCompanyName={product.ownedBy?.name ?? null}
                        onCancel={() => requestExit(productListUrl)}
                        onSave={handleSave}
                        saving={isPersistenceInFlight}
                        saveDisabled={isTransportMaterializationPending || upsertProduct.isCreateIdentityUnknown}
                        exitDisabled={isExitBlocked}
                    />
                )}
                aside={({ stickyTop }) => (
                    <ScoreLiveSidebar
                        existingProduct={existingProduct}
                        product={product}
                        productReference={product.productReference ?? undefined}
                        productReferenceScores={productReferenceScores}
                        mode={scoresMode}
                        olapScores={olapScores}
                        isLoading={scoresLoading}
                        scoreQueryError={scoreQueryError}
                        retryScoreQuery={retryScoreQuery}
                        stickyTop={stickyTop}
                    />
                )}
            >
                <ProductValidationBanner
                    bannerRef={validationBannerRef}
                    product={product}
                    errorsBySection={validationErrors}
                />
                {upsertProduct.isCreateIdentityUnknown && (
                    <Banner variant="error">{t("product.form.save_error", "Unable to save the product.")}</Banner>
                )}
                <ProductFormSections
                    companyIds={companyIds}
                    disabled={isDraftWriteBlocked}
                    getDisplayedSectionScore={getDisplayedSectionScore}
                    ingredientEcsBreakdown={ingredientEcsBreakdown}
                    ingredientLineScores={ingredientLineScores}
                    onTransportMaterializationPendingChange={onTransportMaterializationPendingChange}
                    onPhotoPersistencePendingChange={onPhotoPersistencePendingChange}
                    packagingLineScores={packagingLineScores}
                    product={product}
                    refsByKey={refsByKey}
                    resolvedTransports={resolvedTransports}
                    scoreUnit={scoreUnit}
                    sectionErrors={sectionErrors}
                    setProduct={setProductIfEditable}
                    stepKeys={stepKeys}
                    t={t}
                    transformationLineScores={transformationLineScores}
                    transportLineScores={transportLineScores}
                />
            </EntityFormPage>

            <QuitConfirmDialog
                open={leaveGuard.open}
                isNew={isNew}
                isCreateIdentityUnknown={upsertProduct.isCreateIdentityUnknown}
                confirmDisabled={isExitBlocked}
                onSave={() => {
                    if (isExitCurrentlyBlocked() || upsertProduct.isCreateIdentityUnknown) return;
                    leaveGuard.beginSave();
                    handleSave();
                }}
                onConfirm={() => {
                    if (isExitCurrentlyBlocked()) return;
                    leaveGuard.discard();
                }}
                onCancel={leaveGuard.keepEditing}
            />
        </>
    );
};
